'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { MergeabilityStatus } from './get-mergeability-status.server';
import type { MergeabilityStatusResponse, PendingMergeabilityStatus } from './query-data';

import { mergeabilityStatusQueryKey } from './keys';
import { isPendingMergeabilityStatus } from './query-data';

// PR v2 renders multiple mergeability consumers at once, so keep the result warm for
// a minute to avoid duplicate refetches while users move around the same PR page.
const PR_V2_STALE_TIME_MS = 60_000;
const PR_V2_PENDING_REFETCH_BASE_MS = 2_000;
const PR_V2_PENDING_REFETCH_MAX_MS = 30_000;
const PR_V2_PENDING_MAX_POLLS = 10;

export function useMergeabilityStatus(
  owner: string,
  repo: string,
  number: string,
  options?: {
    initialData?: MergeabilityStatus | null;
    enabled?: boolean;
  },
): {
  status: MergeabilityStatus | null;
  isLoading: boolean;
  isError: boolean;
} {
  const enabled = Boolean(owner && repo && number) && options?.enabled !== false;
  const endpoint = `/api/v2/pull-requests/${owner}/${repo}/${number}/mergeability`;
  const queryKey = mergeabilityStatusQueryKey(owner, repo, number, 'v2');
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const previous = queryClient.getQueryData<MergeabilityStatusResponse>(queryKey);
      const attempt = isPendingMergeabilityStatus(previous) ? (previous.attempt ?? 0) + 1 : 1;
      const response = await fetch(endpoint);
      if (response.status === 202) {
        // 202 means the backend is repairing mirror metadata; keep polling instead of caching
        // null. A `terminal` body flag means this viewer's pending state cannot resolve here.
        const body = (await response.json().catch(() => null)) as { terminal?: boolean } | null;
        return {
          status: 'pending',
          attempt,
          ...(body?.terminal === true ? { terminal: true } : {}),
        } satisfies PendingMergeabilityStatus;
      }
      if (!response.ok) {
        throw new Error('Failed to fetch mergeability status');
      }
      const status = (await response.json()) as MergeabilityStatus | null;
      // Defensive compatibility: older deployments returned `200 null`, which caused the spinner to
      // stick. Treat that shape as pending so a refreshed deployment can recover the UI.
      return status ?? ({ status: 'pending', attempt } satisfies PendingMergeabilityStatus);
    },
    enabled,
    initialData: options?.initialData ?? undefined,
    // A different PR must never inherit the previous query key's merge verdict.
    placeholderData: () => undefined,
    refetchInterval: (query) => {
      // Poll only while the server explicitly says the mirror is not ready. Resolved statuses keep
      // the normal one-minute stale window used by the rest of PR v2.
      const queryData = query.state.data;
      if (!isPendingMergeabilityStatus(queryData)) {
        return false;
      }
      if (queryData.terminal) {
        return false;
      }
      // Back off exponentially and stop after a bounded number of polls so a mirror that never
      // repairs cannot keep every open PR tab on a 2s poll loop forever.
      const attempts = queryData.attempt ?? 1;
      if (attempts >= PR_V2_PENDING_MAX_POLLS) {
        return false;
      }
      return Math.min(
        PR_V2_PENDING_REFETCH_BASE_MS * 2 ** Math.max(attempts - 1, 0),
        PR_V2_PENDING_REFETCH_MAX_MS,
      );
    },
    staleTime: PR_V2_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
  const isPending = isPendingMergeabilityStatus(data);
  const status: MergeabilityStatus | null = isPending ? null : (data ?? null);

  if (!enabled) {
    return { status: null, isLoading: false, isError: false };
  }

  if (isError && !status) {
    return { status: null, isLoading: false, isError: true };
  }

  // Once polling has given up (terminal pending or poll budget exhausted), surface the same
  // fallback UI as a missing status instead of an infinite spinner.
  if (
    isPendingMergeabilityStatus(data) &&
    (data.terminal === true || (data.attempt ?? 0) >= PR_V2_PENDING_MAX_POLLS)
  ) {
    return { status: null, isLoading: false, isError: false };
  }

  if ((isLoading || isPending || data === undefined) && !status) {
    return { status: null, isLoading: true, isError: false };
  }

  return { status, isLoading: false, isError: false };
}
